// DynamicVariablesModal.tsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Collapse, Tabs, Typography, message, Flex } from 'antd';

import CustomModalWithoutButtons from '@/components/CustomModalWithoutButtons';
import SearchInput from '@/components/SearchInput';

import './AgentsTable.css';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CopyOUtlined,
  Shortcut,
} from '@/assets/icons/icons';
import { CustomButton, CustomTitle } from '@/components';
import { useToken } from '@/common/helper';
import { useSpin } from '@/components/SpinContext';
import useUnauthorize from '@/hooks/useUnauthorize';
import { GetAiAgentDynamicVariables } from '@/apis/settings';

const { Text } = Typography;

export type DynamicVariableItem = {
  id: string;
  label: string;
  token: string;
};

export type DynamicVariableGroup = {
  key: string;
  title: string;
  count?: number;
  items: DynamicVariableItem[];
  leftIcon?: React.ReactNode;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onInsert: (token: string, id: string) => void;
  previouslyUsed?: DynamicVariableGroup[];
};

const PREVIOUS_SERIAL_START = 3;

type ApiResponse<T> = {
  data?: {
    data?: {
      data?: T;
      is_success?: boolean;
      message?: string;
      messageTypeId?: string;
      page_count?: number;
    };
  };
};

type ApiDynVarItem = {
  id: string;
  label: string;
  token: string;
  is_archived?: boolean;
};

type ApiDynVarGroup = {
  key: string;
  title: string;
  count?: number;
  items: ApiDynVarItem[];
  is_archived?: boolean;
  sort_order?: number;
};

type ApiDynVarsPayload = {
  all: ApiDynVarGroup[];
  previously_used: ApiDynVarGroup[];
};

const mapApiGroups = (groups: ApiDynVarGroup[]): DynamicVariableGroup[] => {
  return (groups ?? [])
    .filter((g) => !g.is_archived)
    .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999))
    .map((g) => ({
      key: g.key,
      title: g.title,
      count: g.count ?? g.items?.length ?? 0,
      items: (g.items ?? [])
        .filter((it) => !it.is_archived)
        .map((it) => ({ id: it.id, label: it.label, token: it.token })),
    }))
    .filter((g) => g.items.length > 0);
};

const DynamicVariablesModal: React.FC<Props> = ({
  open,
  onClose,
  onInsert,
  previouslyUsed = [],
}) => {
  const { token } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();

  const [tabKey, setTabKey] = useState<'previous' | 'all'>('all');
  const [query, setQuery] = useState('');
  const [allGroups, setAllGroups] = useState<DynamicVariableGroup[]>([]);
  const [prevGroups, setPrevGroups] = useState<DynamicVariableGroup[]>([]);

  const fetchedForOpenRef = useRef(false);
  const lastTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      fetchedForOpenRef.current = false;
      return;
    }
    if (!token) return;

    if (lastTokenRef.current !== token) {
      lastTokenRef.current = token;
      fetchedForOpenRef.current = false;
    }

    if (fetchedForOpenRef.current) return;
    fetchedForOpenRef.current = true;

    showSpin?.();

    GetAiAgentDynamicVariables(token)
      .then((varsRes) => {
        const payload = (varsRes as unknown as ApiResponse<ApiDynVarsPayload>)
          ?.data?.data?.data;

        const nextAll = mapApiGroups(payload?.all ?? []);
        const nextPrevApi = mapApiGroups(payload?.previously_used ?? []);
        const nextPrev = nextPrevApi.length ? nextPrevApi : previouslyUsed;

        setAllGroups(nextAll);
        setPrevGroups(nextPrev);
        setTabKey(nextPrev.length ? 'previous' : 'all');
      })
      .catch((error) => {
        unauthorize?.(error);
        message.error('Failed to load dynamic variables');
        fetchedForOpenRef.current = false;
      })
      .finally(() => {
        hideSpin?.();
      });
  }, [hideSpin, open, previouslyUsed, showSpin, token, unauthorize]);

  const groups = useMemo(() => {
    const source = tabKey === 'previous' ? prevGroups : allGroups;
    const q = query.trim().toLowerCase();
    if (!q) return source;

    return source
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (i) =>
            i.label.toLowerCase().includes(q) ||
            i.token.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0 || g.title.toLowerCase().includes(q));
  }, [tabKey, query, prevGroups, allGroups]);

  const copyToken = async (t: string) => {
    try {
      await navigator.clipboard.writeText(t);
      message.success('Copied');
    } catch {
      message.error('Failed to copy');
    }
  };

  const renderGroupHeader = (
    group: DynamicVariableGroup,
    opts?: { serial?: number; showSerial?: boolean }
  ) => {
    const showSerial = opts?.showSerial ?? true;
    const serial = opts?.serial;

    return (
      <div className='dynVarsGroupHeader'>
        <div className='dynVarsGroupHeaderLeft'>
          {showSerial && typeof serial === 'number' ? (
            <span className='dynVarsSerial'>{serial}.</span>
          ) : null}

          {group.leftIcon ? (
            <span className='dynVarsGroupIcon'>{group.leftIcon}</span>
          ) : null}

          <span className='dynVarsGroupTitle'>
            {group.title}{' '}
            {group.count !== undefined ? (
              <span className='dynVarsGroupCount'>({group.count})</span>
            ) : null}
          </span>
        </div>
      </div>
    );
  };

  const renderRows = (group: DynamicVariableGroup) => (
    <Flex vertical gap={6} className='dynVarsList'>
      {group.items.map((item) => (
        <div className='dynVarsRow' key={`${group.key}-${item.id}`}>
          <span className='dynVarsLabel'>{item.label}</span>

          <span className='dynVarsRight'>
            <Text className='dynVarsToken'>{item.token}</Text>

            <button
              type='button'
              className='dynVarsIconBtn'
              onClick={() => copyToken(item.token)}
            >
              <CopyOUtlined />
            </button>

            <button
              type='button'
              className='dynVarsIconBtn'
              onClick={() => onInsert(item.token, item.id)}
            >
              <Shortcut />
            </button>
          </span>
        </div>
      ))}
    </Flex>
  );

  return (
    <CustomModalWithoutButtons
      modalOpen={open}
      setModalOpen={(v) => !v && onClose()}
      modalTitle=''
      modalSize={560}
      modalBorderRadius='16px'
      fontSize='22px'
      lineHeight='30px'
    >
      <div className='dynVarsModalWrap'>
        <CustomTitle
          text='Dynamic variables'
          fontSize='20px'
          lineHeight='28px'
          fontWeight='700'
          marginBottom='16px'
          marginTop={'20px'}
        />

        <div className='dynVarsSearch'>
          <SearchInput
            width='100%'
            onChange={(val) => setQuery(val)}
            onClear={() => setQuery('')}
          />
        </div>

        <Tabs
          activeKey={tabKey}
          onChange={(k) => setTabKey(k as 'previous' | 'all')}
          items={[
            { key: 'previous', label: 'Previously used' },
            { key: 'all', label: 'All' },
          ]}
          className='dynVarsTabs'
        />

        <div className='dynVarsBody custom-scrollbar'>
          {tabKey === 'previous' ? (
            prevGroups.length ? (
              <Collapse
                bordered={false}
                defaultActiveKey={[groups[0]?.key].filter(Boolean)}
                expandIconPosition='end'
                expandIcon={({ isActive }) =>
                  isActive ? (
                    <ChevronUpIcon size={16} />
                  ) : (
                    <ChevronDownIcon size={16} />
                  )
                }
                className='dynVarsCollapse'
                items={groups.map((group, idx) => ({
                  key: group.key,
                  label: renderGroupHeader(group, {
                    showSerial: true,
                    serial: PREVIOUS_SERIAL_START + idx,
                  }),
                  children: renderRows(group),
                }))}
              />
            ) : (
              <div style={{ padding: 16 }}>No variables found.</div>
            )
          ) : allGroups.length ? (
            <div className='dynVarsAll'>
              {groups.map((group) => (
                <div className='dynVarsAllGroup' key={group.key}>
                  {renderGroupHeader(group, { showSerial: false })}
                  <div className='dynVarsDivider' />
                  {renderRows(group)}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: 16 }}>No variables found.</div>
          )}
        </div>
      </div>

      <div className='dynVarsFooter'>
        <CustomButton
          text='Close'
          bgColor='var(--gray-300)'
          color='var(--text-primary)'
          fontSize='14px'
          fontWeight='600'
          boxShadow='none'
          height='40px'
          width='63px'
          borderRadius='8px'
          onClick={onClose}
        />
      </div>
    </CustomModalWithoutButtons>
  );
};

export default DynamicVariablesModal;
